const { resetTaskState, hasChild } = require('../tasks');
const { hasCircularDependency } = require('./hasCircularDependencies');
const { updateCriticalPath } = require('./updateCriticalPath');
const { updateFloat } = require('./updateFloat');
const { updateForecastedDates } = require('./updateForecastedDates');
const { updateAllTaskProperties } = require('./updateTaskProperties');

const updatedForecastedDateTaskIdsSet = new Set();
let isAutoScheduling = false;

const taskSchedulerEvents = {
  onAfterAutoSchedule: function (taskId, updatedTaskIds) {
    const gantt = this;
    const projectId = taskId ? gantt.getTask(taskId).project_id : undefined;
    const _updatedTaskIds = taskId ? [taskId, ...updatedTaskIds] : updatedTaskIds;

    if (_updatedTaskIds.length > 0) {
      // Get set of all parents of updatedTaskIds
      updateForecastedDates(_updatedTaskIds, gantt).forEach((taskId) =>
        updatedForecastedDateTaskIdsSet.add(taskId)
      );
      // Auto schedule again to see if forecasted dates pushed other tasks
      gantt.autoSchedule();
    } else {
      const updatedForecastedDateTaskIds = [...updatedForecastedDateTaskIdsSet];
      updatedForecastedDateTaskIdsSet.clear();
      gantt.callEvent('onAfterForecastedUpdate', [updatedForecastedDateTaskIds]);
      const updatedPropertiesTaskIds = updateAllTaskProperties(projectId, false, gantt);
      const updatedFloatTaskIds = updateFloat(projectId, false, gantt);
      const updatedCriticalPathTaskIds = updateCriticalPath(projectId, false, gantt);
      const allUpdateTaskIdsSet = new Set([
        ...updatedForecastedDateTaskIds,
        ...updatedPropertiesTaskIds,
        ...updatedFloatTaskIds,
        ...updatedCriticalPathTaskIds,
      ]);
      const allUpdatedTaskIds = [...allUpdateTaskIdsSet];
      gantt.callEvent('onFinishAutoschedule', [allUpdatedTaskIds]);
      // Update all the tasks
      gantt.batchUpdate(() => {
        for (let index = 0; index < allUpdatedTaskIds.length; index++) {
          gantt.updateTask(allUpdatedTaskIds[index]);
        }
      });
      isAutoScheduling = false;
    }
  },
  onBeforeAutoSchedule: function (taskId) {
    if (!isAutoScheduling) {
      isAutoScheduling = true;
      const projectId = taskId && this.getTask(taskId)?.project_id;
      if (
        hasCircularDependency(
          this.getTaskBy((task) => (projectId ? task.project_id === projectId : true)),
          this.getLinks(),
          taskId
        )
      ) {
        console.error('Circular dependencies found. Blocking autoschedule');
        this.callEvent('onAutoScheduleCircularLink', []);
        return false;
      }
    }
    return true;
  },

  onBeforeTaskAutoSchedule: function (task) {
    // Only move todo tasks
    if (task.status === 'todo' && !task?.dates_locked_by) {
      return true;
    } else {
      return false;
    }
  },

  onAfterTaskAutoSchedule: function (task, start, link, predecessor) {
    // Move parent tasks scheduled date
    if (this.hasChild(task.id)) {
      task.scheduled_end_date = this.calculateEndDate({
        start_date: start,
        duration: task.work_days,
        task: task,
      });
      if (task.autoschedule_date === 'schedule') {
        task.scheduled_end_date = task.end_date;
      } else {
        task.forecasted_end_date = task.end_date;
      }
    } else if (task.status === 'todo') {
      // Sync task dates
      task.forecasted_start_date = task.start_date;
      task.scheduled_end_date = task.end_date;
      task.forecasted_end_date = task.end_date;
    }
  },

  onBeforeTaskMove: function (id, parentId, tindex) {
    if (parentId != this.config.root_id) {
      const task = this.getTask(id);
      task.$old_parent = task.parent;
      return true;
    } else {
      return false;
    }
  },

  onAfterTaskMove: function (id, parentId, tindex) {
    const gantt = this;
    const task = this.getTask(id);

    if (gantt.isTaskExists(task.$old_parent)) {
      // Reset the $old_parent's state so that it is now todo
      if (!hasChild(task.$old_parent, gantt)) resetTaskState(this.getTask(task.$old_parent));

      gantt.batchUpdate(() => {
        updateForecastedDates([task.$old_parent], gantt).forEach((taskId) =>
          gantt.updateTask(taskId)
        );
      }, false);
    }

    gantt.autoSchedule(id);
  },

  onAfterTaskAdd: function (id, task) {
    if (task.type !== 'placeholder' && this.isTaskExists(task.parent)) {
      this.autoSchedule(id);
    }
  },

  onAfterTaskDelete: function (id, task) {
    // If the task exists and the parent is not a 'project_bar' (a task with an id less than 0), then we check to see if it is a parent or if it has children, and update its state accordingly
    if (this.isTaskExists(task.parent) && task.parent > 0) {
      const parentTask = this.getTask(task.parent);

      // If the task does not have a child, reset the parentTask (so it is no longer active), and autoSchedule the parent
      if (!this.hasChild(parentTask.id)) {
        resetTaskState(parentTask);
      }

      // Update parent forecasted dates
      this.autoSchedule(task.parent);
    }
  },

  onAfterLinkDelete: function (id, link) {
    if (this.isTaskExists(link.source)) {
      this.autoSchedule(link.source);
    }
  },
};

module.exports = { taskSchedulerEvents };
